'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { InstallmentTerms } from '@/components/ui/installment-terms.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { EntitySpecialtiesAndRqe } from '@/app/(main)/(pages)/_components/entities/entity-specialties-and-rqe.component';
import { PartnerProcedureProfessional } from '@/app/(main)/(pages)/(institucional)/(index)/parceiros/_services/get-partner-procedures-from-search-api.service';
import { SearchCardClinicTabs } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/variants/clinic/_components/tabs/search-card-clinic-tabs.component';
import { SearchEntityRatingComponent } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-rating.component';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { ScheduleTabAnalytics } from '@/app/(main)/(pages)/busca/[slug]/_utils/schedule-tab-analytics.util';

interface EntityProcedureProfessionalCardProps {
  professional: PartnerProcedureProfessional;
  tabAnalytics: ScheduleTabAnalytics;
  position: number;
}

export function EntityProcedureProfessionalCard({
  professional,
  tabAnalytics,
  position,
}: EntityProcedureProfessionalCardProps) {
  const { schedule, totalPrice } = professional;
  const { professional: professionalData, partner } = schedule;

  const hasProfessionalName = Boolean(professionalData.name);
  const canLinkToProfessional = Boolean(professionalData.slug) && hasProfessionalName;
  const displayName = hasProfessionalName ? professionalDisplayName(professionalData) : partner.socialName;
  const professionalHref = `/profissionais/${professionalData.slug}/`;
  const avatarAnalyticsProperties = JSON.stringify({
    entity: tabAnalytics.entity,
    entityType: tabAnalytics.entityType,
    professional_id: professionalData.id,
    posicao: position,
  });

  const avatar = canLinkToProfessional
    ? (
        <EntityAvatar
          size="4xl"
          className="w-22 h-22"
          alt={`Foto de perfil de ${displayName}`}
          type={EntityTypeEnum.PROFESSIONAL}
          src={professionalData.avatar}
        />
      )
    : (
        <EntityAvatar
          size="4xl"
          className="w-22 h-22"
          alt={`Foto de perfil de ${displayName}`}
          type={EntityTypeEnum.LABORATORY_COLLECT_POINT}
          src={partner.avatar}
        />
      );

  return (
    <div
      className="flex w-full flex-col gap-4 rounded-2xl border border-gray-300 bg-white py-2 xl:pb-6 xl:pt-4"
      data-testid={DataTestIdEnum.ENTITY_PROCEDURE_PROFESSIONAL_CARD}
    >
      <div className="flex flex-col gap-2 px-3 xl:px-6">
        <div className="flex gap-3 xl:gap-4">
          {canLinkToProfessional
            ? (
                <Link
                  prefetch={false}
                  href={professionalHref}
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_PROFESSIONAL_AVATAR}
                  data-analytics-properties={avatarAnalyticsProperties}
                >
                  {avatar}
                </Link>
              )
            : avatar}

          <div className="min-w-0 flex-1">
            {canLinkToProfessional
              ? (
                  <Link
                    prefetch={false}
                    href={professionalHref}
                    className="block"
                    data-testid={DataTestIdEnum.ENTITY_PROCEDURE_PROFESSIONAL_CARD_NAME}
                  >
                    <Typography
                      variant={TypographyVariant.MEDIUM_SUBTITLE}
                      className="font-semibold text-green-500 hover:underline [word-break:break-word]"
                    >
                      {displayName}
                    </Typography>
                  </Link>
                )
              : (
                  <Typography
                    variant={TypographyVariant.MEDIUM_SUBTITLE}
                    className="font-semibold text-green-500 [word-break:break-word]"
                    data-testid={DataTestIdEnum.ENTITY_PROCEDURE_PROFESSIONAL_CARD_NAME}
                  >
                    {displayName}
                  </Typography>
                )}

            <SearchEntityRatingComponent rating={professionalData.rating} hidesEmptyState />
          </div>
        </div>

        <EntitySpecialtiesAndRqe professional={professionalData} />
      </div>

      <div
        className="border-y border-gray-300 px-3 py-4 xl:border-y-0 xl:px-6 xl:py-0"
        data-testid={DataTestIdEnum.ENTITY_PROCEDURE_PROFESSIONAL_CARD_SCHEDULES}
      >
        <div className="xl:p-4 xl:rounded-lg xl:border xl:border-gray-300 xl:bg-gray-50">
          <SearchCardClinicTabs schedule={schedule} tabAnalytics={tabAnalytics} />
        </div>
      </div>

      <div className="px-3 xl:px-6">
        <InstallmentTerms
          totalPrice={totalPrice}
          dataTestId={DataTestIdEnum.ENTITY_PROCEDURE_PROFESSIONAL_CARD_INSTALLMENT}
        />
      </div>
    </div>
  );
}
